import { useNavigate } from "react-router-dom";
import "./AdScan.scss";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiSearch, FiUploadCloud } from "react-icons/fi";
import {
    DialogActions,
    Dialog,
    DialogTitle,
    DialogContent,
} from "@mui/material";
import { showToast } from "../../../toastService.js";
import { motion } from "framer-motion";
import { useSelector } from "react-redux";
import newRequest from "../../../utils/newRequest.js";
import { ScaleLoader } from "react-spinners";
import { CustomLoadingButton } from "../../../components/ui/CustomButton.jsx";
import ConfirmDialog from "../../../components/confirmDialog/confirmDialog.jsx";
import { MdSave, MdDelete } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { generateUniqueCode, generateUniqueId, logData } from "../../../utils/genericFunctions.js";
import { customSelectStyles_dark, customSelectStyles_light } from "../../../components/ui/CustomSelect.jsx";
import Select from "react-select";


const isPdf = (fileOrName, mime = "") => {
    const name = typeof fileOrName === "string" ? fileOrName : fileOrName?.name || "";
    const m = (mime || fileOrName?.type || "").toLowerCase();
    return m.includes("pdf") || name.toLowerCase().endsWith(".pdf");
};

const isImage = (fileOrName, mime = "") => {
    const name = typeof fileOrName === "string" ? fileOrName : fileOrName?.name || "";
    const m = (mime || fileOrName?.type || "").toLowerCase();
    return m.startsWith("image/") || /\.(png|jpg|jpeg|webp|gif)$/i.test(name);
};

const AdScan = () => {
    const { t } = useTranslation();
    const { currentUser } = useSelector((state) => state.user);
    const { currentTheme } = useSelector((state) => state.theme);
    const navigate = useNavigate();
    const [scanTypes, setScanTypes] = useState([]);
    const [selectedScanType, setSelectedScanType] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isProcessingTitle, setIsProcessingTitle] = useState(t('pleaseWait'));
    // uploading
    const [isUploading, setIsUploading] = useState(false);
    const [uploadPct, setUploadPct] = useState(0);
    // single-file state
    const [localFile, setLocalFile] = useState(null); // File
    const [localPreviewUrl, setLocalPreviewUrl] = useState(null); // objectURL
    const [uploadedFile, setUploadedFile] = useState(null); // backend file object

    const [extracted, setExtracted] = useState(null);
    const [draftFields, setDraftFields] = useState(null);

    useEffect(() => {
        const f = extracted?.result?.fields;
        setDraftFields(f ? JSON.parse(JSON.stringify(f)) : null); // deep clone
    }, [extracted?.documentId]);

    // UI state
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef(null);

    // dialogs (kept from template)
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [confirmationActiontext, setConfirmationActiontext] = useState("");
    const [isNotifyDialogOpen, setIsNotifyDialogOpen] = useState(false);
    const [notifyDialogTitle, setNotifyDialogTitle] = useState("Confirm");
    const [notifyDialogText, setNotifyDialogText] = useState("Text");

    const handleConfirm = async () => {
        setIsConfirmDialogOpen(false);
    };

    const fetchScanTypes = async () => {
        try {
            const res = await newRequest.post("/sysCode/findAll", { sys_type: "Scan Types" });

            const options = res.data?.map((val) => ({
                value: val?.sys_code,
                label: val?.sys_name,
            }));

            setScanTypes(options);
        } catch (error) {
            console.error("Error fetching entity types:", error);
        }
    };

    // cleanup preview url
    useEffect(() => {
        return () => {
            if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);
        };
    }, [localPreviewUrl]);

    const clearSelected = useCallback(() => {
        if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);
        setLocalFile(null);
        setLocalPreviewUrl(null);
        setUploadedFile(null);
        setUploadPct(0);
        if (fileInputRef.current) fileInputRef.current.value = "";
    }, [localPreviewUrl]);

    const validateSingleFile = (file) => {
        if (!file) return { ok: false, msg: "No file selected." };
        const maxMb = 30;
        const sizeMb = file.size / (1024 * 1024);
        if (sizeMb > maxMb) return { ok: false, msg: `File too large. Max ${maxMb}MB.` };

        if (!isPdf(file) && !isImage(file)) {
            return { ok: false, msg: "Only PDF or image files are supported." };
        }
        return { ok: true };
    };

    const uploadToBackend = async (file, fileFolder) => {
        if (!file) throw new Error("No file provided");

        const formData = new FormData();

        // match what your backend expects (based on your previous working uploader)
        formData.append("folder", fileFolder || "scans");
        const uniqueDoc = generateUniqueId();
        logData("uniqueDoc", uniqueDoc);
        formData.append("documentType", "Scan" || "");
        formData.append("documentNo", uniqueDoc || "");
        formData.append("documentId", uniqueDoc || "");
        formData.append("renameFile", "Y");   // only for scans

        // keep file field name EXACTLY what multer expects:
        // if your backend is upload.single("file"), then "file" is correct
        formData.append("file", file);

        // optional extra metadata (only if backend uses them)
        formData.append("orgCode", currentUser?.usr_org_code || "");
        formData.append("userCode", currentUser?.usr_id || "");

        try {
            const res = await newRequest.post("/uploadFiles/upload", formData, {
                headers: { "Content-Type": "multipart/form-data" },
                onUploadProgress: (evt) => {
                    if (!evt.total) return;
                    setUploadPct(Math.round((evt.loaded / evt.total) * 100));
                },
            });
            // logData("res", res.data);
            setUploadedFile({
                _id: res.data?.file?.fl_parent_doc_id,
                url: res.data?.url,
                internalName: res.data?.internalName,
                originalName: res.data?.originalName,
                orgCode: res.data?.orgCode,
                folder: res.data?.folder,
                size: res.data?.size,
                mimetype: res.data?.mimetype,
            });
            return res.data;
        } catch (err) {
            // helpful debug
            console.error("uploadToBackend failed:", {
                status: err?.response?.status,
                data: err?.response?.data,
                message: err?.message,
            });
            throw err;
        }
    };

    const pickAndUpload = useCallback(
        async (file) => {
            const v = validateSingleFile(file);
            if (!v.ok) {
                showToast(v.msg, "warning");
                return;
            }

            // clear previous
            clearSelected();

            // set local preview immediately (fast UX)
            const objUrl = URL.createObjectURL(file);
            setLocalFile(file);
            setLocalPreviewUrl(objUrl);

            // upload
            try {
                await uploadToBackend(file, "scans");
                showToast("File uploaded", "success");
            } catch (e) {
                console.error(e);
                showToast("Upload failed", "error");
            }
        },
        [clearSelected, uploadToBackend]
    );

    // input change
    const onPickFile = async (e) => {
        const file = e.target.files?.[0] || null;
        if (!file) return;
        await pickAndUpload(file);
    };

    // drag drop
    const onDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);

        const file = e.dataTransfer?.files?.[0] || null;
        if (!file) return;
        await pickAndUpload(file);
    };

    const onDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(true);
    };

    const onDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
    };

    const previewTitle = useMemo(() => {
        if (localFile?.name) return localFile.name;
        if (uploadedFile?.fl_name) return uploadedFile.fl_name;
        return "";
    }, [localFile?.name, uploadedFile?.fl_name]);

    useEffect(() => {
        fetchScanTypes();
    }, []);

    const pct = (v) => {
        const n = Number(v);
        if (!Number.isFinite(n)) return 0;
        return Math.max(0, Math.min(100, Math.round(n * 100)));
    };

    const money = (v) => {
        if (v === null || v === undefined || v === "") return "";
        const n = Number(v);
        if (!Number.isFinite(n)) return String(v);
        return n.toLocaleString();
    };

    const pretty = (v) => {
        if (v === null || v === undefined) return "";
        if (Array.isArray(v)) return v.length ? JSON.stringify(v) : "";
        if (typeof v === "object") return JSON.stringify(v);
        return String(v);
    };

    const normalizeScanType = (s) => String(s || "").trim().toLowerCase();

    const SeverityPill = ({ severity = "low", text }) => (
        <span className={`scan-pill ${String(severity).toLowerCase()}`}>
            {text}
        </span>
    );


    const toNumOrNull = (v) => {
        if (v === "" || v === null || v === undefined) return null;
        const s = String(v).replace(/,/g, "").trim();
        if (!s) return null;
        const n = Number(s);
        return Number.isFinite(n) ? n : null;
    };

    const setByPath = (obj, path, value) => {
        const parts = path.split(".");
        const next = Array.isArray(obj) ? obj.slice() : { ...(obj || {}) };
        let cur = next;

        for (let i = 0; i < parts.length - 1; i++) {
            const k = parts[i];
            const isIdx = String(Number(k)) === k;
            const key = isIdx ? Number(k) : k;

            const existing = cur?.[key];
            const cloned =
                Array.isArray(existing) ? existing.slice() :
                    existing && typeof existing === "object" ? { ...existing } :
                        (String(Number(parts[i + 1])) === parts[i + 1] ? [] : {});

            cur[key] = cloned;
            cur = cur[key];
        }

        const last = parts[parts.length - 1];
        const lastKey = String(Number(last)) === last ? Number(last) : last;
        cur[lastKey] = value;
        return next;
    };

    const EditableField = ({
        label,
        value,
        onChange,
        type = "text",
        placeholder = "—",
    }) => {
        return (
            <div className="scan-kv">
                <div className="scan-k">{label}</div>
                <div className="scan-v">
                    <input
                        className="scan-input"
                        type={type}
                        value={value ?? ""}
                        placeholder={placeholder}
                        onChange={(e) => onChange(e.target.value)}
                    />
                </div>
            </div>
        );
    };

    const EditableTextarea = ({ label, value, onChange, placeholder = "—" }) => (
        <div className="scan-kv" style={{ gridColumn: "1 / -1" }}>
            <div className="scan-k">{label}</div>
            <div className="scan-v">
                <textarea
                    className="scan-textarea"
                    value={value ?? ""}
                    placeholder={placeholder}
                    onChange={(e) => onChange(e.target.value)}
                    rows={4}
                />
            </div>
        </div>
    );

    const ExtractedPanel = ({
        extracted,
        draftFields,
        setDraftFields,
        onSaveDraft, // optional callback
    }) => {
        const scanType = extracted?.scanType || "";
        const fields = draftFields || {};
        const confidence = extracted?.result?.confidence;
        const anomaly = extracted?.result?.anomaly;

        const type = String(scanType || "").toLowerCase();
        const confPct = Math.max(0, Math.min(100, Math.round(Number(confidence || 0) * 100)));
        const isAnom = !!anomaly?.isAnomaly;

        const update = (path, raw, kind = "text") => {
            let val = raw;

            // normalize numeric fields
            if (kind === "number") val = toNumOrNull(raw);

            setDraftFields((prev) => setByPath(prev || {}, path, val));
        };

        const Header = () => (
            <>
                <div className="scan-badges">
                    <div className="scan-conf">
                        <span className="scan-badge-title">Confidence</span>
                        <span className={`scan-badge ${confPct >= 85 ? "ok" : confPct >= 65 ? "warn" : "bad"}`}>
                            {confPct}%
                        </span>
                    </div>

                    <div className="scan-anom">
                        <span className="scan-badge-title">Anomaly</span>
                        <span className={`scan-badge ${isAnom ? "bad" : "ok"}`}>{isAnom ? "YES" : "NO"}</span>
                        {anomaly?.severity ? (
                            <span className={`scan-pill ${String(anomaly.severity).toLowerCase()}`}>
                                {String(anomaly.severity).toUpperCase()}
                            </span>
                        ) : null}
                    </div>
                </div>

                <div className="scan-confbar">
                    <div className="scan-confbar-fill" style={{ width: `${confPct}%` }} />
                </div>
            </>
        );

        const Flags = () => {
            const flags = Array.isArray(anomaly?.flags) ? anomaly.flags : [];
            if (!flags.length) return null;
            return (
                <div className="scan-anom-flags">
                    <div className="scan-section-title">Flags</div>
                    <ul>
                        {flags.map((f, i) => (
                            <li key={`${String(f)}-${i}`}>{String(f)}</li>
                        ))}
                    </ul>
                </div>
            );
        };

        const ExpenseReceipt = () => {
            const items = Array.isArray(fields.items) ? fields.items : [];

            return (
                <>
                    <div className="scan-section-title">Receipt</div>
                    <div className="scan-grid">
                        <EditableField label="Vendor" value={fields.vendor_name} onChange={(v) => update("vendor_name", v)} />
                        <EditableField label="Branch" value={fields.vendor_branch} onChange={(v) => update("vendor_branch", v)} />
                        <EditableField label="Vendor PIN" value={fields.vendor_pin} onChange={(v) => update("vendor_pin", v)} />
                        <EditableField label="Receipt No" value={fields.receipt_number} onChange={(v) => update("receipt_number", v)} />
                        <EditableField label="Tax Invoice No" value={fields.tax_invoice_number} onChange={(v) => update("tax_invoice_number", v)} />
                        <EditableField label="Date/Time" value={fields.receipt_date_time} onChange={(v) => update("receipt_date_time", v)} />
                        <EditableField label="Currency" value={fields.currency} onChange={(v) => update("currency", v)} />
                        <EditableField label="Served By" value={fields.served_by} onChange={(v) => update("served_by", v)} />
                        <EditableField label="Payment Method" value={fields.payment_method} onChange={(v) => update("payment_method", v)} />
                        <EditableField label="Payment Ref" value={fields.payment_reference} onChange={(v) => update("payment_reference", v)} />
                    </div>

                    <div className="scan-section-title">Totals</div>
                    <div className="scan-grid">
                        <EditableField label="Sub Total (Excl)" value={fields.sub_total_excl} onChange={(v) => update("sub_total_excl", v, "number")} />
                        <EditableField label="Tax" value={fields.tax_amount} onChange={(v) => update("tax_amount", v, "number")} />
                        <EditableField label="Levies" value={fields.levies} onChange={(v) => update("levies", v, "number")} />
                        <EditableField label="Gratuity" value={fields.gratuity} onChange={(v) => update("gratuity", v, "number")} />
                        <EditableField label="Discount" value={fields.discount} onChange={(v) => update("discount", v, "number")} />
                        <EditableField label="Total" value={fields.total_amount} onChange={(v) => update("total_amount", v, "number")} />
                        <EditableField label="Amount Paid" value={fields.amount_paid} onChange={(v) => update("amount_paid", v, "number")} />
                        <EditableField label="Balance" value={fields.balance} onChange={(v) => update("balance", v, "number")} />
                    </div>

                    <div className="scan-section-title">Items</div>
                    {items.length === 0 ? (
                        <div className="scan-muted">No items extracted.</div>
                    ) : (
                        <div className="scan-items edit">
                            <div className="scan-items-head">
                                <div>Name</div>
                                <div className="num">Qty</div>
                                <div className="num">Price</div>
                                <div className="num">Line Total</div>
                            </div>

                            {items.map((it, idx) => {
                                const q = Number(it?.quantity);
                                const p = Number(it?.price);
                                const lineTotal = Number.isFinite(q) && Number.isFinite(p) ? q * p : "";

                                return (
                                    <div className="scan-items-row" key={`${it?.name || "item"}-${idx}`}>
                                        <input
                                            className="scan-input"
                                            value={it?.name ?? ""}
                                            onChange={(e) => update(`items.${idx}.name`, e.target.value)}
                                            placeholder="Item name"
                                        />
                                        <input
                                            className="scan-input num"
                                            value={it?.quantity ?? ""}
                                            onChange={(e) => update(`items.${idx}.quantity`, e.target.value, "number")}
                                            placeholder="0"
                                        />
                                        <input
                                            className="scan-input num"
                                            value={it?.price ?? ""}
                                            onChange={(e) => update(`items.${idx}.price`, e.target.value, "number")}
                                            placeholder="0"
                                        />
                                        <div className="num">{lineTotal !== "" ? Number(lineTotal).toLocaleString() : "—"}</div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <EditableTextarea label="Notes" value={fields.notes} onChange={(v) => update("notes", v)} />
                </>
            );
        };

        const Logbook = () => (
            <>
                <div className="scan-section-title">Vehicle</div>
                <div className="scan-grid">
                    <EditableField label="Reg No" value={fields.reg_no} onChange={(v) => update("reg_no", v)} />
                    <EditableField label="Chassis No" value={fields.chassis_no} onChange={(v) => update("chassis_no", v)} />
                    <EditableField label="Engine No" value={fields.engine_no} onChange={(v) => update("engine_no", v)} />
                    <EditableField label="Make" value={fields.make} onChange={(v) => update("make", v)} />
                    <EditableField label="Model" value={fields.model} onChange={(v) => update("model", v)} />
                    <EditableField label="Body Type" value={fields.body_type} onChange={(v) => update("body_type", v)} />
                    <EditableField label="Vehicle Type" value={fields.vehicle_type} onChange={(v) => update("vehicle_type", v)} />
                    <EditableField label="Man Year" value={fields.man_year} onChange={(v) => update("man_year", v)} />
                    <EditableField label="Rating (cc)" value={fields.rating} onChange={(v) => update("rating", v)} />
                    <EditableField label="Color" value={fields.color} onChange={(v) => update("color", v)} />
                    <EditableField label="Passengers" value={fields.passengers} onChange={(v) => update("passengers", v)} />
                    <EditableField label="Tare Weight (kg)" value={fields.tare_weight} onChange={(v) => update("tare_weight", v)} />
                    <EditableField label="Previous Reg" value={fields.previous_reg} onChange={(v) => update("previous_reg", v)} />
                </div>

                <div className="scan-section-title">Owner</div>
                <div className="scan-grid">
                    <EditableField label="Owner Name" value={fields.owner_name} onChange={(v) => update("owner_name", v)} />
                    <EditableField label="Owner PIN" value={fields.owner_pin} onChange={(v) => update("owner_pin", v)} />
                </div>
            </>
        );

        const RiskNote = () => (
            <>
                <div className="scan-section-title">Policy</div>
                <div className="scan-grid">
                    <EditableField label="Insured Name" value={fields.insured_name} onChange={(v) => update("insured_name", v)} />
                    <EditableField label="Policy No" value={fields.policy_number} onChange={(v) => update("policy_number", v)} />
                    <EditableField label="Period From" value={fields.period_from} onChange={(v) => update("period_from", v)} />
                    <EditableField label="Period To" value={fields.period_to} onChange={(v) => update("period_to", v)} />
                    <EditableField label="Sum Insured" value={fields.sum_insured} onChange={(v) => update("sum_insured", v, "number")} />
                    <EditableField label="Premium" value={fields.premium} onChange={(v) => update("premium", v, "number")} />
                    <EditableField label="Risk Location" value={fields.risk_location} onChange={(v) => update("risk_location", v)} />
                    <EditableField label="Broker" value={fields.broker_name} onChange={(v) => update("broker_name", v)} />
                    <EditableField label="Insurer" value={fields.insurance_company} onChange={(v) => update("insurance_company", v)} />
                </div>

                <div className="scan-section-title">Risk</div>
                <EditableTextarea
                    label="Risk Description"
                    value={fields.risk_description}
                    onChange={(v) => update("risk_description", v)}
                />
            </>
        );

        const renderByType = () => {
            if (!extracted) return <div className="scan-muted">No extraction yet.</div>;
            if (type.includes("expense") || type.includes("receipt")) return <ExpenseReceipt />;
            if (type.includes("logbook")) return <Logbook />;
            if (type.includes("risk")) return <RiskNote />;
            return <div className="scan-muted">Unsupported scan type: {scanType}</div>;
        };

        return (
            <div className="scan-doc-card">
                <div className="scan-doc-card-head">
                    <div className="scan-doc-card-title">Extracted Data</div>
                    <div className="scan-doc-card-sub">{scanType ? `Type: ${scanType}` : "Upload and process a document."}</div>
                    {extracted ? <Header /> : null}
                </div>

                <div className="scan-doc-card-body scan-doc-card-body-scroll">
                    {!extracted ? <div className="scan-doc-empty">No extraction yet.</div> : renderByType()}
                    {extracted ? <Flags /> : null}
                </div>

                {extracted ? (
                    <div className="scan-doc-card-actions">
                        <CustomLoadingButton
                            currentTheme={currentTheme}
                            iconPosition="end"
                            borderRadiusType="both"
                            variant="tint"
                            color="#0ea5a4"
                            tooltip="Save edited fields"
                            endIcon={<MdSave />}
                            onClick={() => onSaveDraft?.(draftFields)}
                        >
                            Save
                        </CustomLoadingButton>
                    </div>
                ) : null}
            </div>
        );
    };

    return (
        <div>
            {isLoading ? (
                <div className="no-content">
                    <ScaleLoader
                        color="var(--primary)"
                        height={50}
                        radius={8}
                        width={4}
                    />
                </div>
            ) : (
                <motion.div
                    className="ad-scan"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { duration: 0.6 } }}
                    exit={{ opacity: 0, transition: { duration: 0.4 } }}
                >
                    <div className="ad-scan-navbar">
                        <div className="ad-scan-navbar-left">
                            <span className="page-title">{t('scan')}</span>
                        </div>
                        <div className="ad-scan-navbar-right">
                            <div className="input-item-with-label">
                                <Select
                                    id="scan_type"
                                    name="scan_type"
                                    menuPortalTarget={document.body}
                                    styles={{
                                        ...(currentTheme === "dark" ? customSelectStyles_dark : customSelectStyles_light),
                                        menuPortal: base => ({
                                            ...base,
                                            zIndex: 9999,
                                        })
                                    }}
                                    width="200px"
                                    value={selectedScanType}
                                    onChange={(selectedOption) => setSelectedScanType(selectedOption)}
                                    options={scanTypes}
                                    isSearchable={true}
                                    placeholder={t('type').toUpperCase()}
                                />
                            </div>
                            <CustomLoadingButton
                                currentTheme={currentTheme}
                                iconPosition="end"
                                borderRadiusType="both"
                                variant="tint"
                                color="#00D37F"
                                tooltip={!uploadedFile?._id ? "Upload file first" : "Process uploaded file"}
                                endIcon={<MdSave />}
                                onClick={async () => {
                                    try {
                                        if (!uploadedFile?._id) return showToast("Upload a document first", "warning");
                                        // showToast("Next: run extraction", "info");
                                        setIsProcessing(true);
                                        const res = await newRequest.get(`/scan/extract/${uploadedFile?._id}`, {
                                            params: { scanType: selectedScanType?.value || selectedScanType?.label },
                                        });
                                        setIsProcessing(false);
                                        setExtracted(res.data);
                                        logData("extracted", res.data);
                                    } catch (error) {
                                        console.log(error);
                                        setIsProcessing(false);
                                    }
                                }}
                                disabled={!uploadedFile?._id}
                            >
                                {t("process")}
                            </CustomLoadingButton>
                        </div>
                    </div>

                    <div className="ad-scans-content">
                        <div className="scan-doc-layout">
                            {/* LEFT: upload + preview */}
                            <div className="scan-doc-left">
                                <div
                                    className={`scan-doc-drop ${dragOver ? "drag" : ""} ${localFile ? "hasfile" : ""}`}
                                    onClick={() => fileInputRef.current?.click()}
                                    onDrop={onDrop}
                                    onDragOver={onDragOver}
                                    onDragLeave={onDragLeave}
                                    role="button"
                                    tabIndex={0}
                                >
                                    {!localFile ? (
                                        <div className="scan-doc-drop-inner">
                                            <div className="scan-doc-icon">
                                                <FiUploadCloud />
                                            </div>
                                            <div className="scan-doc-title">Upload document</div>
                                            <div className="scan-doc-sub">
                                                Drag & drop a PDF/Image here, or click to browse.
                                            </div>

                                            <div className="scan-doc-hint">
                                                Only one file at a time.
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="scan-doc-preview">
                                            <div className="scan-doc-preview-head">
                                                <div className="scan-doc-filename" title={previewTitle}>
                                                    {previewTitle}
                                                </div>

                                                <button
                                                    type="button"
                                                    className="scan-doc-clear"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        clearSelected();
                                                        setExtracted(null);
                                                    }}
                                                    title="Remove file"
                                                >
                                                    <MdDelete />
                                                </button>
                                            </div>

                                            <div className="scan-doc-preview-body">
                                                {isImage(localFile) && (
                                                    <img src={localPreviewUrl} alt={localFile.name} />
                                                )}

                                                {isPdf(localFile) && (
                                                    <iframe
                                                        title={localFile.name}
                                                        src={localPreviewUrl}
                                                        className="scan-doc-iframe"
                                                    />
                                                )}
                                            </div>

                                            <div className="scan-doc-uploadbar">
                                                {isUploading ? (
                                                    <>
                                                        <div className="scan-doc-uploadmeta">
                                                            <span>Uploading…</span>
                                                            <span>{uploadPct}%</span>
                                                        </div>
                                                        <div className="scan-doc-bar">
                                                            <div className="scan-doc-fill" style={{ width: `${uploadPct}%` }} />
                                                        </div>
                                                    </>
                                                ) : uploadedFile?._id ? (
                                                    <div className="scan-doc-uploadmeta ok">
                                                        Uploaded ✓
                                                    </div>
                                                ) : (
                                                    <div className="scan-doc-uploadmeta warn">
                                                        Not uploaded
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="application/pdf,image/*"
                                        onChange={onPickFile}
                                        style={{ display: "none" }}
                                    />
                                </div>
                            </div>

                            {/* RIGHT: placeholder for extracted form (next step) */}
                            <div className="scan-doc-right">
                                <ExtractedPanel
                                    extracted={extracted}
                                    draftFields={draftFields}
                                    setDraftFields={setDraftFields}
                                    onSaveDraft={(payload) => {
                                        console.log("save payload", payload);
                                        // call your backend: /scan/save or attach to transaction
                                        // newRequest.post("/scan/save", { documentId: extracted.documentId, scanType: extracted.scanType, fields: payload })
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                </motion.div>
            )}

            {/* Notify dialog */}
            <Dialog
                open={isNotifyDialogOpen}
                onClose={() => setIsNotifyDialogOpen(false)}
                PaperProps={{
                    sx: {
                        backgroundColor: currentTheme === "dark" ? "#181A20" : "#fff",
                        color: currentTheme === "dark" ? "#fff" : "#000",
                    },
                }}
                slotProps={{
                    backdrop: {
                        sx: { backgroundColor: "rgba(0, 0, 0, 0.7)" },
                    },
                }}
            >
                <DialogTitle>{notifyDialogTitle}</DialogTitle>
                <DialogContent>
                    <div dangerouslySetInnerHTML={{ __html: notifyDialogText }} />
                </DialogContent>
                <DialogActions>
                    <CustomLoadingButton onClick={() => setIsNotifyDialogOpen(false)}>
                        {t("close")}
                    </CustomLoadingButton>
                </DialogActions>
            </Dialog>

            {/* Confirm dialog */}
            <ConfirmDialog
                isOpen={isConfirmDialogOpen}
                onClose={() => setIsConfirmDialogOpen(false)}
                onConfirm={handleConfirm}
                dialogTitle={t("confirmation")}
                dialogContent={<p dangerouslySetInnerHTML={{ __html: confirmationActiontext }} />}
            />
            {isProcessing && (
                <motion.div
                    className="processing-dialog"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { duration: 1 } }}
                    exit={{ opacity: 0, transition: { duration: 1 } }}
                >
                    <div className="processing-wrapper">
                        <span className='main-title'>{isProcessingTitle || t('pleaseWait')}</span>
                        <ScaleLoader
                            color="var(--primary)"
                            height={50}
                            radius={8}
                            width={4}
                        />
                    </div>
                </motion.div>)}
        </div>
    );
};

export default AdScan;